import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    Row,
    RowSelectionState,
    TableMeta,
    Updater,
    useReactTable,
} from '@tanstack/react-table';
import { CircleLoader } from '../CircleLoader';
import { LoadingOverlay } from '../helpers/LoadingOverlay';
import { TableColumnHeaderActions, TableEmptyState, TableEmptyStateContainer, TablePagination } from './components';
import { useTableColumns } from './hooks';
import { PrintStyles } from './styles/print-style';
import {
    TableBody,
    TableBodyRow,
    TableCell,
    TableCellInner,
    TableContainer,
    TableElement,
    TableHeader,
    TableHeaderCell,
    TableHeaderRow,
    TableWrapper,
} from './styles/style';
import { TableProvider, useTableContext } from './TableContext';
import { TableFilters, TableState } from './types';

export interface TableProps<T> {
    data: T[];
    uniqueRowId: keyof T;
    loading: boolean;
    amountOfRows: number;
    fixedFirstColumn?: boolean;
    fixedHeader?: boolean;
    rowSelection?: {
        rowSelection: RowSelectionState;
        onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
        enableRowSelection?: boolean | ((row: Row<T>) => boolean);
    };
    meta?: TableMeta<T>;
    emptyStatePrimaryText: React.ReactNode;
    emptyStateSecondaryText: React.ReactNode;
}

export const Table = <T, F extends TableFilters<T> = TableFilters<T>>({
    fixedFirstColumn = false,
    fixedHeader = false,
    amountOfRows,
    data,
    loading: isLoading,
    rowSelection: rowSelectionObject,
    meta,
    uniqueRowId,
    emptyStatePrimaryText,
    emptyStateSecondaryText,
}: TableProps<T>) => {
    const { rowSelection, onRowSelectionChange, enableRowSelection } = rowSelectionObject || {};
    const rowSelectionEnabled = !!rowSelectionObject && enableRowSelection !== false;
    const { state } = useTableContext<T, F>();

    const table = useReactTable({
        columns: useTableColumns<T>({ enableRowSelection: rowSelectionEnabled }),
        data,
        defaultColumn: {
            enableSorting: false,
            enableColumnFilter: false,
        },
        getCoreRowModel: getCoreRowModel(),
        debugTable: true,
        enableRowSelection,
        getRowId: (row) => row[uniqueRowId] as string,
        meta,
        onRowSelectionChange,
        state: {
            rowSelection,
        },
    });

    const pageCount = Math.ceil(amountOfRows / state.pagination.pageSize) ?? -1;
    const hasData = data.length > 0;

    const renderEmpty = () => {
        if (hasData) {
            return null;
        }
        return !isLoading ? (
            <TableEmptyState
                emptyStatePrimaryText={emptyStatePrimaryText}
                emptyStateSecondaryText={emptyStateSecondaryText}
            />
        ) : (
            <TableEmptyStateContainer />
        );
    };

    return (
        <TableWrapper aria-disabled={isLoading}>
            <TableContainer>
                <TableElement>
                    <TableHeader fixedHeader={fixedHeader}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableHeaderRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const sortable = header.column.getCanSort();
                                    const filterable = header.column.getCanFilter();

                                    return (
                                        <TableHeaderCell
                                            key={header.id}
                                            fixedFirstColumn={fixedFirstColumn}
                                            fixedHeader={fixedHeader}
                                            rowSelectionEnabled={rowSelectionEnabled}
                                            width={header.column.columnDef.meta?.width}
                                            interactive={sortable || filterable}
                                            filterable={filterable}
                                        >
                                            <TableColumnHeaderActions
                                                // @ts-expect-error
                                                header={header}
                                                align={header.column.columnDef.meta?.align}
                                                setRowSelection={onRowSelectionChange}
                                                sortable={sortable}
                                                filterable={filterable}
                                            />
                                        </TableHeaderCell>
                                    );
                                })}
                            </TableHeaderRow>
                        ))}
                    </TableHeader>
                    <TableBody isLoading={isLoading}>
                        {table.getRowModel().rows.map((row, index, rows) => (
                            <TableBodyRow
                                key={row.id}
                                active={rowSelection && row.id in rowSelection}
                                nextIsActive={rowSelection && rows[index + 1]?.id in rowSelection}
                                previousIsActive={rowSelection && rows[index - 1]?.id in rowSelection}
                                data-test-table-row={row.id}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        rowSelectionEnabled={rowSelectionEnabled}
                                        fixedFirstColumn={fixedFirstColumn}
                                    >
                                        <TableCellInner align={cell.column.columnDef.meta?.align}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCellInner>
                                    </TableCell>
                                ))}
                            </TableBodyRow>
                        ))}
                    </TableBody>
                </TableElement>
            </TableContainer>

            {state.pagination.show && Number.isFinite(state.pagination.pageSize) ? (
                <TablePagination size={pageCount} totalCount={amountOfRows} />
            ) : null}

            {renderEmpty()}

            {isLoading ? (
                <LoadingOverlay show={isLoading}>
                    <CircleLoader />
                </LoadingOverlay>
            ) : null}
            <PrintStyles enableRowSelection={rowSelectionEnabled} />
        </TableWrapper>
    );
};

interface StandaloneTableProps<T, F extends TableFilters<T> = TableFilters<T>> extends TableProps<T> {
    columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    state: TableState<T, F>;
    onStateChange: (state: TableState<T, F>) => void;
}

export const StandaloneTable = <T, F extends TableFilters<T>>(props: StandaloneTableProps<T, F>) => (
    <TableProvider columns={props.columns} state={props.state} onStateChange={props.onStateChange}>
        <Table {...props} />
    </TableProvider>
);
